import { loadProfile, loadSa } from '../api/profile.js';
import { lastPractisedISO, recentSessions, sessionCount } from '../api/practice-history.js';
import { loadKnownRagaNames } from './ear-known-ragas.js';

/**
 * Connected-Swarasense (W4): the personalized welcome strip — the returning
 * visitor's front door, layered (progressive enhancement) over the crawlable
 * landing hero. Reads the on-device profile + history; a brand-new visitor
 * (nothing saved) gets NOTHING here and sees the full marketing hero instead.
 *
 * For a returning visitor it shows: "Welcome back · Sa {note} · {n} sessions ·
 * last {date}", a **Continue practising** button that restores the LAST raga +
 * level (cohesion review addition #3 — not just a count), and your ragas as
 * quick jump-in chips. If the shruti was never explicitly set (still the
 * default), a gentle, dismissible **"set your shruti"** nudge (addition #2).
 *
 * Kid-first: warm and number-free except the session count (allowed). No CLS —
 * it renders once, after its async reads resolve, into a reserved container.
 */

export interface HomeStripDeps {
  /** Restore a raga (+ level) and bring the tool into view — wired by app.ts to
   * set the store and scroll. */
  onContinue: (raga: string, level?: string) => void;
  /** Open the shruti / "Your voice" control (the first-run nudge target). */
  onSetShruti: () => void;
}

const NUDGE_DISMISS_KEY = 'swarasense.saNudgeDismissed';

function nudgeDismissed(): boolean {
  try {
    return localStorage.getItem(NUDGE_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}
function dismissNudge(): void {
  try {
    localStorage.setItem(NUDGE_DISMISS_KEY, '1');
  } catch {
    /* private mode — fine, it just won't persist the dismissal */
  }
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

/** Friendly date from an ISO `YYYY-MM-DD` — today / yesterday / "5 Aug".
 * Best-effort; returns the raw string on any trouble. */
function friendlyDate(iso: string): string {
  try {
    const todayISO = new Date().toISOString().slice(0, 10);
    if (iso === todayISO) return 'today';
    const y = new Date();
    y.setDate(y.getDate() - 1);
    if (iso === y.toISOString().slice(0, 10)) return 'yesterday';
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
}

/**
 * Render the strip into `container` (progressive enhancement). Async: reads
 * history, then renders once. Renders nothing for a brand-new visitor.
 */
export async function mountHomeStrip(container: HTMLElement, deps: HomeStripDeps): Promise<void> {
  const profile = loadProfile();
  const [count, last, recent] = await Promise.all([
    sessionCount(),
    lastPractisedISO(),
    recentSessions(1),
  ]);
  const ragas = loadKnownRagaNames();

  // Returning signal: any on-device history or explicit personalization. A
  // truly fresh visitor falls through to the crawlable hero (renders nothing).
  const returning =
    count > 0 || !!profile.sa || !!profile.level || (profile.goals?.length ?? 0) > 0 || ragas.length > 0;
  if (!returning) {
    container.innerHTML = '';
    return;
  }

  const sa = loadSa();
  const saExplicit = !!profile.sa;
  const lastRaga = recent[0]?.raga;
  const lastLevel = recent[0]?.level;
  const showNudge = !saExplicit && !nudgeDismissed();

  const metaBits: string[] = [`Sa <b>${esc(sa.pitchClass)}${sa.octave}</b>`];
  if (count > 0) {
    metaBits.push(`${count} ${count === 1 ? 'session' : 'sessions'}`);
    if (last) metaBits.push(`last practised ${esc(friendlyDate(last))}`);
  }

  const ragaChips = ragas
    .slice(0, 5)
    .map(
      (name) =>
        `<button type="button" class="home-strip-raga" data-raga="${esc(name)}">${esc(name)}</button>`,
    )
    .join('');

  container.innerHTML = `
    <section class="home-strip" aria-label="Welcome back">
      <div class="home-strip-main">
        <p class="home-strip-greeting">Welcome back <span class="home-strip-meta">· ${metaBits.join(' · ')}</span></p>
        <div class="home-strip-actions">
          ${
            lastRaga
              ? `<button type="button" class="btn primary home-strip-continue" data-raga="${esc(
                  lastRaga,
                )}" data-level="${esc(lastLevel ?? '')}">Continue ${esc(lastRaga)} <span aria-hidden="true">→</span></button>`
              : ''
          }
          ${ragaChips ? `<span class="home-strip-ragas">${ragaChips}</span>` : ''}
        </div>
      </div>
      ${
        showNudge
          ? `<div class="home-strip-nudge" role="note">
               <span>Make it yours — set your <b>shruti</b> so everything's in your voice.</span>
               <button type="button" class="btn home-strip-nudge-set">Set my shruti</button>
               <button type="button" class="home-strip-nudge-dismiss" aria-label="Dismiss">&times;</button>
             </div>`
          : ''
      }
    </section>`;

  const continueBtn = container.querySelector<HTMLButtonElement>('.home-strip-continue');
  continueBtn?.addEventListener('click', () => {
    deps.onContinue(continueBtn.dataset.raga!, continueBtn.dataset.level || undefined);
  });
  for (const chip of container.querySelectorAll<HTMLButtonElement>('.home-strip-raga')) {
    chip.addEventListener('click', () => deps.onContinue(chip.dataset.raga!, lastLevel));
  }
  container
    .querySelector<HTMLButtonElement>('.home-strip-nudge-set')
    ?.addEventListener('click', () => deps.onSetShruti());
  container.querySelector<HTMLButtonElement>('.home-strip-nudge-dismiss')?.addEventListener('click', () => {
    dismissNudge();
    container.querySelector('.home-strip-nudge')?.remove();
  });
}
